import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { SAVE, COMPONENT, DASH_VIEW } from 'utils/constants'
import { useDispatch } from 'react-redux'
import { useSelector, useMakeSelector } from 'hooks/redux'
import {
  getLocationDefinition,
  getLayoutLocationToUpdate,
} from 'selectors/dash'
import { setSelectedIdWithDrawerAndMeta } from 'actions/navigator'
import { makeComponentMetaSelector } from 'selectors/navigator'
import {
  setLayoutLocationToUpdate,
  updateLocation,
  resetLocation,
} from 'actions/dash'
import { Content, Footer } from 'components/SwitchAndGauge/DashDrawer/Sections'
import ChecPopConfirm from 'components/patterns/ChecPopConfirm'
import {
  Button,
  Header,
  Paragraph,
  NumberInput,
  TextInput,
  SelectMenu,
  FlexCol,
  FlexRow,
} from 'components/patterns'
import * as yup from 'yup'
import strings from 'utils/strings'
import styled from '@emotion/styled'
import { themeColor } from 'utils/styles'
import { StyledViewWrap } from './style'

const LayoutLocationEditor = () => {
  const dispatch = useDispatch()

  const { id: location, isDirty = false } = useMakeSelector(
    makeComponentMetaSelector,
    {
      id: DASH_VIEW,
    }
  )
  const locationData = useSelector(getLocationDefinition, location)
  const locationToUpdateData = useSelector(getLayoutLocationToUpdate)

  const seed = locationToUpdateData || locationData

  const [formData, setFormData] = useState(seed)
  const [dirty, setDirty] = useState(false)
  const [previewed, setPreviewed] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(isDirty)

  const {
    name = '',
    type = '',
    detail = '',
    panel = '',
    parent = '',
    children = [],
    top = 0,
    left = 0,
    width = 0,
    height = 0,
    zIndex = 5,
  } = formData

  const updateFormData = (name, value) => {
    setFormData((data) => ({
      ...data,
      [name]: value,
    }))
  }

  const handleOnPreview = () => {
    setPreviewed(true)
    setDirty(false)
    dispatch(updateLocation(formData))
  }

  const handleOnSave = () => {
    dispatch(setLayoutLocationToUpdate(formData))
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: SAVE,
      })
    )
  }

  const handleOnChangeLocationData = (name) => (e) => {
    updateFormData(name, e)
    setDirty(true)
    setPreviewed(false)
    setShowCancelConfirm(true)
  }

  const handleOnCancelConfirm = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: COMPONENT,
        id: location,
      })
    )
    dispatch(resetLocation(location))
  }

  const disablePreviewBtn = !dirty
  // isDirty is set externally, in this case from the SaveView component
  const disableSaveBtn = isDirty ? false : !(previewed && !dirty)

  return (
    <StyledViewWrap>
      <Content>
        <div>
          <FlexRow>
            <FlexCol span={24}>
              <ViewHeader type="h2">
                {strings.edit_location}: {name}
              </ViewHeader>
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol span={24}>
              <SectionHeader bold size="small">
                {strings.information}
              </SectionHeader>
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol span={8}>
              <SectionInput>
                <TextInput
                  name="name"
                  label="Name"
                  value={name}
                  disabled
                  rules={yup.string()}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <TextInput
                  name="detail"
                  label="Detail"
                  value={detail}
                  rules={yup.string()}
                  onChange={handleOnChangeLocationData('detail')}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <TextInput
                  name="type"
                  label="Type"
                  value={type}
                  rules={yup.string()}
                  onChange={handleOnChangeLocationData('type')}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <TextInput
                  name="base panel"
                  label="Base Panel"
                  value={panel}
                  rules={yup.string()}
                  onChange={handleOnChangeLocationData('panel')}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <TextInput
                  name="parent location"
                  label="Parent Location"
                  value={parent}
                  rules={yup.string()}
                  onChange={handleOnChangeLocationData('parent')}
                />
              </SectionInput>
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <SectionDivider />
          </FlexRow>
          <FlexRow>
            <FlexCol span={24}>
              <SectionHeader bold size="small">
                {strings.properties}
              </SectionHeader>
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol span={8}>
              <SectionInput>
                <NumberInput
                  name="left"
                  label="X"
                  value={left}
                  rules={yup.number()}
                  type="number"
                  onChange={handleOnChangeLocationData('left')}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <NumberInput
                  name="top"
                  label="Y"
                  value={top}
                  rules={yup.number()}
                  type="number"
                  onChange={handleOnChangeLocationData('top')}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <SelectMenu
                  label="Layer"
                  name="z index"
                  onChange={handleOnChangeLocationData('zIndex')}
                  value={parseInt(zIndex)}
                  rules={yup.number()}
                  width={88}
                  options={[
                    {
                      label: '1',
                      value: 5,
                    },
                    {
                      label: '2',
                      value: 10,
                    },
                    {
                      label: '3',
                      value: 15,
                    },
                  ].map(({ label, value }) => ({
                    key: label,
                    value,
                    title: <span>{label}</span>,
                  }))}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <NumberInput
                  name="width"
                  label="Width"
                  value={width}
                  rules={yup.number()}
                  type="number"
                  onChange={handleOnChangeLocationData('width')}
                />
              </SectionInput>
            </FlexCol>
            <FlexCol span={8}>
              <SectionInput>
                <NumberInput
                  name="height"
                  label="Height"
                  value={height}
                  rules={yup.number()}
                  type="number"
                  onChange={handleOnChangeLocationData('height')}
                />
              </SectionInput>
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <SectionDivider />
          </FlexRow>
          <FlexRow>
            <FlexCol span={24}>
              <SectionHeader bold size="small">
                {strings.child_locations}
              </SectionHeader>
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol>
              {children.length > 0 ? (
                children.map((child) => <div key={child}>{child}</div>)
              ) : (
                <Paragraph>{strings.none}</Paragraph>
              )}
            </FlexCol>
          </FlexRow>
        </div>
      </Content>
      <Footer>
        <FlexRow>
          <FlexCol span={12}>
            <div>
              <ChecPopConfirm
                showConfirm={showCancelConfirm}
                title={
                  <div style={{ width: 300 }}>
                    {strings.edit_location_cancel}
                  </div>
                }
                okText={strings.abandon_my_changes}
                okType="danger"
                cancelType="primary"
                cancelText={strings.continue_editing}
                onConfirm={handleOnCancelConfirm}
                placement="top"
              >
                <Button type="secondary">{strings.cancel}</Button>
              </ChecPopConfirm>
            </div>
          </FlexCol>
          <FlexCol span={12} flexDirection="row" justifyContent="flex-end">
            <div>
              <Button
                onClick={handleOnPreview}
                type="primary"
                disabled={disablePreviewBtn}
              >
                {strings.preview}
              </Button>
            </div>
            <div>
              <Button
                onClick={handleOnSave}
                type="primary"
                disabled={disableSaveBtn}
              >
                {strings.save}
              </Button>
            </div>
          </FlexCol>
        </FlexRow>
      </Footer>
    </StyledViewWrap>
  )
}

export default LayoutLocationEditor

LayoutLocationEditor.propTypes = {
  id: PropTypes.string,
  // this is set from externally save layout location
  isDirty: PropTypes.bool,
}

const ViewHeader = styled(Header)`
  margin-bottom: 16px;
`

const SectionHeader = styled(Paragraph)`
  margin-bottom: 4px;
`

const SectionInput = styled.div`
  margin-bottom: 4px;
`

const SectionDivider = styled.div`
  border-bottom: 1px solid ${themeColor('grey')};
  height: 0;
  margin: 16px 0;
  width: 100%;
`
